import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useOptimistic,
  startTransition,
} from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import auth from '@/shared/config/auth.config.js';

const AuthContext = createContext(null);

const AUTH_ACTIONS = {
  INIT_SUCCESS: 'INIT_SUCCESS',
  INIT_ERROR: 'INIT_ERROR',
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_ERROR: 'LOGIN_ERROR',
  SIGNUP_START: 'SIGNUP_START',
  SIGNUP_SUCCESS: 'SIGNUP_SUCCESS',
  SIGNUP_ERROR: 'SIGNUP_ERROR',
  LOGOUT_START: 'LOGOUT_START',
  LOGOUT_SUCCESS: 'LOGOUT_SUCCESS',
  REFRESH_USER: 'REFRESH_USER',
};

const initialState = {
  user: null,
  isLoading: false,
  isInitialized: false,
  error: null,
};

function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.INIT_SUCCESS:
      return {
        ...state,
        user: action.payload,
        isInitialized: true,
        isLoading: false,
      };
    case AUTH_ACTIONS.INIT_ERROR:
      return {
        ...state,
        user: null,
        isInitialized: true,
        isLoading: false,
        error: action.payload,
      };
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.SIGNUP_START:
    case AUTH_ACTIONS.LOGOUT_START:
      return { ...state, isLoading: true, error: null };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.SIGNUP_SUCCESS:
      return { ...state, user: action.payload, isLoading: false, error: null };
    case AUTH_ACTIONS.LOGIN_ERROR:
    case AUTH_ACTIONS.SIGNUP_ERROR:
      return { ...state, user: null, isLoading: false, error: action.payload };
    case AUTH_ACTIONS.LOGOUT_SUCCESS:
      return { ...state, user: null, isLoading: false, error: null };
    case AUTH_ACTIONS.REFRESH_USER:
      return { ...state, user: action.payload };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [optimisticUser, setOptimisticUser] = useOptimistic(state.user);

  const queryClient = useQueryClient();

  useEffect(() => {
    const initAuth = () => {
      try {
        const userData = auth.getUserFromToken();
        dispatch({ type: AUTH_ACTIONS.INIT_SUCCESS, payload: userData });
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        dispatch({ type: AUTH_ACTIONS.INIT_ERROR, payload: error.message });
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (phone_number, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const result = await auth.login(phone_number, password);
      const userData = auth.getUserFromToken();

      startTransition(() => {
        dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: userData });
      });
      return result;
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.LOGIN_ERROR, payload: error.message });
      throw error;
    }
  }, []);

  const signup = useCallback(
    async (phone_number, password, password_confirm) => {
      dispatch({ type: AUTH_ACTIONS.SIGNUP_START });
      try {
        const result = await auth.signup(
          phone_number,
          password,
          password_confirm,
        );
        dispatch({ type: AUTH_ACTIONS.SIGNUP_SUCCESS, payload: null });
        return result;
      } catch (error) {
        dispatch({ type: AUTH_ACTIONS.SIGNUP_ERROR, payload: error.message });
        throw error;
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    setOptimisticUser(null);
    dispatch({ type: AUTH_ACTIONS.LOGOUT_START });
    try {
      await auth.logout();

      queryClient.clear();

      startTransition(() => {
        dispatch({ type: AUTH_ACTIONS.LOGOUT_SUCCESS });
      });
    } catch (error) {
      console.error('Logout error:', error);
      queryClient.clear();
      dispatch({ type: AUTH_ACTIONS.LOGOUT_SUCCESS });
    }
  }, [setOptimisticUser, queryClient]);

  const refreshUser = useCallback(() => {
    try {
      const userData = auth.getUserFromToken();
      startTransition(() => {
        dispatch({ type: AUTH_ACTIONS.REFRESH_USER, payload: userData });
      });
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  }, []);

  const value = useMemo(
    () => ({
      user: optimisticUser || state.user,
      isAuthenticated: !!(optimisticUser || state.user),
      isLoading: state.isLoading,
      isInitialized: state.isInitialized,
      error: state.error,
      login,
      signup,
      logout,
      refreshUser,
    }),
    [
      optimisticUser,
      state.user,
      state.isLoading,
      state.isInitialized,
      state.error,
      login,
      signup,
      logout,
      refreshUser,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
